// Restaurant-wide arrangement of the dashboard product icons, stored in
// restaurantData/{restaurantId}.dashboardProductLayout. Without it the
// dashboard keeps its default order.
export const PRODUCT_LAYOUT_FIELD = 'dashboardProductLayout';
// Version 2 added `shown`. Builds that only know version 1 refuse to save
// over a newer layout, so they cannot drop it.
export const PRODUCT_LAYOUT_VERSION = 2;
const READABLE_LAYOUT_VERSIONS = [1, PRODUCT_LAYOUT_VERSION];

export type ProductLayout = {
  order: string[];
  hidden: string[];
  /** Products shown only once added via "Hinzufügen" (see hiddenUntilAdded). */
  shown?: string[];
  /** Dashboard banners (e.g. the Sia promotion) removed in edit mode. */
  hiddenBanners?: string[];
};

export type StoredProductLayout = ProductLayout & {
  version: number;
  updatedAt?: number;
  updatedBy?: string | null;
};

export type LayoutCandidate = {
  id: string;
  available: boolean;
  showWhenUnavailable: boolean;
  /** Closes the default grid, after the promotional tiles (Support). */
  last?: boolean;
  /** Not in the grid until a restaurant adds it on purpose (Menu). */
  hiddenUntilAdded?: boolean;
};

export type ProductArrangement = {
  grid: string[];
  /** Hidden, but usable: reachable through "Mehr anzeigen". */
  folder: string[];
  hidden: string[];
  /** Products waiting to be added, not hidden by anybody's choice. */
  defaultHidden: string[];
  /** What the "Hinzufügen" overlay lists. */
  addable: string[];
};

const uniqueIds = (value: unknown): string[] =>
  Array.isArray(value)
    ? Array.from(
        new Set(
          value.filter((id): id is string => typeof id === 'string' && !!id),
        ),
      )
    : [];

export const storedLayoutVersion = (raw: unknown): number | null =>
  !!raw && typeof raw === 'object' && typeof (raw as any).version === 'number'
    ? (raw as any).version
    : null;

export const isReadableLayout = (raw: unknown) =>
  READABLE_LAYOUT_VERSIONS.includes(storedLayoutVersion(raw) ?? -1);

export function readProductLayout(raw: unknown): ProductLayout | null {
  if (!isReadableLayout(raw)) return null;
  const stored = raw as StoredProductLayout;
  const hidden = uniqueIds(stored.hidden);
  const visible = (id: string) => !hidden.includes(id);
  return {
    order: uniqueIds(stored.order).filter(visible),
    hidden,
    shown: uniqueIds(stored.shown).filter(visible),
    hiddenBanners: uniqueIds(stored.hiddenBanners),
  };
}

export function arrangeProducts(
  candidates: LayoutCandidate[],
  layout: ProductLayout | null,
): ProductArrangement {
  const byId = new Map(candidates.map((c) => [c.id, c]));
  const hidden = (layout?.hidden ?? []).filter((id) => byId.has(id));
  const defaultHidden = candidates
    .filter(
      (c) =>
        c.hiddenUntilAdded &&
        !layout?.shown?.includes(c.id) &&
        !hidden.includes(c.id),
    )
    .map((c) => c.id);
  const heldBack = [...hidden, ...defaultHidden];
  const showable = (c: LayoutCandidate) =>
    !heldBack.includes(c.id) && (c.available || c.showWhenUnavailable);
  const defaultGrid = [
    ...candidates.filter((c) => !c.last && c.available && showable(c)),
    ...candidates.filter((c) => !c.last && !c.available && showable(c)),
    ...candidates.filter((c) => c.last && showable(c)),
  ].map((c) => c.id);

  const stored = (layout?.order ?? []).filter((id) =>
    defaultGrid.includes(id),
  );
  const grid = [...stored, ...defaultGrid.filter((id) => !stored.includes(id))];

  return {
    grid,
    folder: heldBack.filter((id) => byId.get(id)?.available),
    hidden,
    defaultHidden,
    addable: [
      ...heldBack,
      ...candidates
        .filter((c) => !c.available && !heldBack.includes(c.id))
        .map((c) => c.id),
    ],
  };
}

export function moveProduct(
  layout: ProductLayout,
  id: string,
  toIndex: number,
): ProductLayout {
  if (!layout.order.includes(id)) return layout;
  const order = layout.order.filter((x) => x !== id);
  const index = Math.max(0, Math.min(order.length, toIndex));
  order.splice(index, 0, id);
  return { ...layout, order };
}

export const hideProduct = (
  layout: ProductLayout,
  id: string,
): ProductLayout => ({
  ...layout,
  order: layout.order.filter((x) => x !== id),
  hidden: [...layout.hidden.filter((x) => x !== id), id],
  shown: layout.shown?.filter((x) => x !== id),
});

export const restoreProduct = (
  layout: ProductLayout,
  id: string,
  addedOnRequest = false,
): ProductLayout => ({
  ...layout,
  order: [...layout.order.filter((x) => x !== id), id],
  hidden: layout.hidden.filter((x) => x !== id),
  shown: addedOnRequest
    ? [...(layout.shown ?? []).filter((x) => x !== id), id]
    : layout.shown,
});

export const sameLayout = (a: ProductLayout, b: ProductLayout) =>
  a.order.join('|') === b.order.join('|') &&
  a.hidden.join('|') === b.hidden.join('|') &&
  (a.shown ?? []).join('|') === (b.shown ?? []).join('|') &&
  (a.hiddenBanners ?? []).join('|') === (b.hiddenBanners ?? []).join('|');

export const hideBanner = (
  layout: ProductLayout,
  id: string,
): ProductLayout => ({
  ...layout,
  hiddenBanners: [...(layout.hiddenBanners ?? []).filter((x) => x !== id), id],
});

export const restoreBanner = (
  layout: ProductLayout,
  id: string,
): ProductLayout => ({
  ...layout,
  hiddenBanners: (layout.hiddenBanners ?? []).filter((x) => x !== id),
});

/**
 * The draft with each of `ids` back at its place in `saved` (the stored
 * layout, or the editor's previous draft) after the product it followed
 * there, when the draft lost it because it could not be shown, e.g. while its
 * availability is still being asked. Never one the draft hides.
 */
export function withSavedPositions(
  draft: ProductLayout,
  saved: ProductLayout | null,
  ids: string[],
): ProductLayout {
  if (!saved) return draft;
  const order = [...draft.order];
  saved.order.forEach((id, index) => {
    if (!ids.includes(id) || order.includes(id) || draft.hidden.includes(id)) return;
    const previous = saved.order
      .slice(0, index)
      .reverse()
      .find((before) => order.includes(before));
    order.splice(previous ? order.indexOf(previous) + 1 : 0, 0, id);
  });
  return { ...draft, order };
}

/**
 * Moves `id` to `toIndex` of the shown grid. Products of `keepPlaceOf` that are
 * not shown stay where they were in the draft (keyboard and drag alike).
 */
export function moveShownProduct(
  layout: ProductLayout,
  candidates: LayoutCandidate[],
  id: string,
  toIndex: number,
  keepPlaceOf: string[],
): ProductLayout {
  const shown = moveProduct(
    { ...layout, order: arrangeProducts(candidates, layout).grid },
    id,
    toIndex,
  );
  return withSavedPositions(shown, layout, keepPlaceOf);
}

/** Keeps entries of products this dashboard version does not know. */
export function layoutForSave(
  draft: ProductLayout,
  stored: ProductLayout | null,
  knownIds: string[],
): Required<ProductLayout> {
  const unknown = (ids: string[] = []) =>
    ids.filter((id) => !knownIds.includes(id));
  return {
    order: uniqueIds([...draft.order, ...unknown(stored?.order)]),
    hidden: uniqueIds([...draft.hidden, ...unknown(stored?.hidden)]),
    shown: uniqueIds([...(draft.shown ?? []), ...unknown(stored?.shown)]),
    // The draft starts from every stored banner id, known or not.
    hiddenBanners: uniqueIds(draft.hiddenBanners),
  };
}
